// Dedicated /pricing page — the canonical, linkable pricing destination
// (the SaaS-standard pattern). It reuses the existing self-serve pricing
// cards (AgencyPricingPlans, with all its OAuth + checkout wiring intact),
// then adds a feature comparison table and a pricing FAQ beneath them.
//
// The homepage keeps only a compact teaser (PricingTeaserBand) that links
// here, so the full detail lives in exactly one place.
//
// Because the Enterprise "Contact us" card and the FAQ "Talk to us" link
// fire the openEmbedInquiry() window event, this page mounts its own
// <EmbedInquiryModal> + listener (the same pattern the landing uses).

import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, ArrowRight } from 'lucide-react';
import ToolFunnelHeader from '@/components/tools/ToolFunnelHeader';
import Footer from '@/components/Footer';
import { AgencyPricingPlans } from '@/components/lead-gen-website/AgencyPricingPlans';
import { PricingFaq } from '@/components/lead-gen-website/PricingFaq';
import { EmbedInquiryModal } from '@/components/lead-gen-website/EmbedInquiryModal';
import { openEmbedInquiry } from '@/components/lead-gen-website/openEmbedInquiry';
import { FloatingAccountPill } from '@/components/auth/FloatingAccountPill';

// Factual reassurance chips for the hero. Every line is sourced from the
// real plan terms (Free needs no card, month-to-month cancellation, INR
// billing with a GST invoice, top-up credits never expire) — no invented
// social proof.
const HERO_CHIPS = [
  'No card to start',
  'Cancel anytime',
  'USD or INR (GST invoice)',
  '14-day free trial',
];

export default function Pricing() {
  // Embed-inquiry modal, lifted to the page root so the Enterprise card and
  // the FAQ "Talk to us" link can open it from anywhere via openEmbedInquiry().
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquirySource, setInquirySource] = useState('pricing');
  const [inquiryVariant, setInquiryVariant] = useState<'embed' | 'talk'>('talk');

  useEffect(() => {
    const onOpen = (e: Event) => {
      const ce = e as CustomEvent<{ source?: string; variant?: 'embed' | 'talk' }>;
      setInquirySource(ce.detail?.source || 'pricing');
      setInquiryVariant(ce.detail?.variant || 'talk');
      setInquiryOpen(true);
    };
    window.addEventListener('open-embed-inquiry', onOpen);
    return () => window.removeEventListener('open-embed-inquiry', onOpen);
  }, []);

  return (
    <div className="bg-gray-950 text-white antialiased">
      <Helmet>
        <title>Pricing | Fly Audit and Lead-Gen Tools for Agencies</title>
        <meta
          name="description"
          content="Simple, transparent pricing for agencies. Start with a 14-day free trial of 25 scans, then Starter at $9.99 a month or Pro at $19.99."
        />
        <link rel="canonical" href="https://www.fly-social.com/pricing" />
        <meta property="og:title" content="Pricing | Fly Audit and Lead-Gen Tools for Agencies" />
        <meta
          property="og:description"
          content="Simple, transparent pricing for agencies. Start with a 14-day free trial of 25 scans, then Starter at $9.99 a month or Pro at $19.99."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fly-social.com/pricing" />
      </Helmet>

      {/* Stripped funnel header (logo + single CTA), identical to the agency
          homepage and the tool funnel pages. We intentionally do NOT use the
          corporate SiteNavBar here so /pricing stays inside the focused funnel. */}
      <ToolFunnelHeader />

      {/* Floating account pill — bottom-right, only renders for signed-in
          users WITH an active trial / paid plan / lifetime. First-time
          signed-in users (no agency) and signed-out visitors see nothing. */}
      <FloatingAccountPill context="pricing" />

      {/* Hero — the single page header (AgencyPricingPlans below renders with
          showHeader=false so there is no duplicate heading). Extra top padding
          clears the fixed funnel logo. Ambient violet/fuchsia glow gives the
          dark brand some cinematic depth without animation. */}
      <header className="relative overflow-hidden bg-gray-950 px-6 pt-36 pb-10 text-center lg:px-8 lg:pt-44 lg:pb-14">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(139,92,246,0.22), transparent 60%), radial-gradient(ellipse 50% 40% at 80% 20%, rgba(236,72,153,0.12), transparent 55%)',
          }}
        />
        <div className="relative mx-auto max-w-3xl">
          <h1
            className="font-bold tracking-tight text-white"
            style={{ fontSize: 'clamp(2.4rem, 5vw, 4rem)', lineHeight: 1.05 }}
          >
            Simple, transparent pricing.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-white/60">
            Start with 25 free scans, then pick Starter at $9.99 a month or Pro
            at $19.99 a month as your lead flow grows. One scan per audit,
            whatever the tool.
          </p>

          <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2.5">
            {HERO_CHIPS.map((chip) => (
              <li
                key={chip}
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-sm text-white/75"
              >
                <Check className="size-3.5 shrink-0 text-emerald-400" />
                {chip}
              </li>
            ))}
          </ul>
        </div>
      </header>

      <main>
        {/* The plan cards render without their own header here, so this
            keeps the outline h1 > h2 > h3 for screen readers. */}
        <h2 className="sr-only">Plans</h2>
        <AgencyPricingPlans showHeader={false} showTrialCta={false} showFreeCard={true} />
        <PricingFaq />

        {/* Closing CTA — the final conversion step the pricing pattern calls
            for. Reuses the existing inquiry modal for "talk to us"; the
            secondary action jumps back to the plan cards (id="pricing"). */}
        <section className="relative overflow-hidden bg-gray-950 py-24 text-center lg:py-28">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 55% 50% at 50% 100%, rgba(139,92,246,0.18), transparent 60%)',
            }}
          />
          <div className="relative mx-auto max-w-2xl px-6 lg:px-8">
            <h2
              className="font-bold tracking-tight text-white"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', lineHeight: 1.1 }}
            >
              Not sure which plan fits?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-white/60">
              Tell us about your agency and we'll point you to the right tier.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => openEmbedInquiry('pricing-final-cta', 'talk')}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-black/30 transition-transform hover:scale-105"
              >
                Talk to our team
                <ArrowRight className="size-4" />
              </button>
              <button
                type="button"
                onClick={() =>
                  document
                    .getElementById('pricing')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                Compare plans
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer compact />

      <EmbedInquiryModal
        open={inquiryOpen}
        onOpenChange={setInquiryOpen}
        source={inquirySource}
        title={inquiryVariant === 'talk' ? 'Talk to our team' : 'Get your embed code'}
        subtitle={
          inquiryVariant === 'talk'
            ? "Tell us about your agency and we'll set up a quick call this week."
            : "Tell us about your agency and we'll send your white-labeled embed within 24 hours."
        }
        ctaLabel={inquiryVariant === 'talk' ? 'Request a call' : 'Get my embed code'}
      />
    </div>
  );
}
